import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { Loader2, type LucideIcon } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: LucideIcon;
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon: Icon,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={clsx(
          'inline-flex items-center justify-center gap-2 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          {
            'rounded-xl px-3 py-1.5 text-xs': size === 'sm',
            'rounded-xl px-4 py-2.5 text-sm': size === 'md',
            'rounded-xl px-6 py-3 text-base': size === 'lg',
          },
          {
            'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700':
              variant === 'primary',
            'border border-line bg-surface text-ink hover:border-line-strong hover:bg-surface-2':
              variant === 'secondary',
            'text-ink-muted hover:bg-surface-2 hover:text-ink':
              variant === 'ghost',
            'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500':
              variant === 'danger',
          },
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          Icon && <Icon className="h-4 w-4 shrink-0" />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
